// The customer says they already shipped the part back (2026-10-28,
// docs/decisions/2026-10-28-already-shipped-back-refund.md;
// docs/pipeline-behaviors.md "Already shipped back").
//
// A customer who already sent the part back (in transit or delivered to us,
// with or without a tracking number) and asks for a refund:
//   - the disposition is RMA (the part comes back), whatever the scrap check
//     said; the check still runs and shows as overridden
//     ("Overridden from Scrap RTN: the customer says the part was already
//     returned (UPS <tracking>).");
//   - the customer pays for their own shipping: no label, no deduction;
//   - the customer's tracking is kept on the step, linked to the carrier;
//   - the reply is the client's Refund folder template, chosen from whether
//     an RMA already exists for the parts and whether the return is
//     delivered or in transit (refundStageTemplateKey()).
// One function decides each of these, used by the pipeline, the agent's step
// and Other Actions alike, and by the panels (the disposition names, the
// override text, the note's amounts line).
//
// Pure: no calls. Tracking numbers are kept only with a carrier's format and
// check digit (tracking-number.js).
import { validTrackingNumber, trackingPageUrl } from './tracking-number.js'

// --- The disposition names (2026-10-28) ------------------------------------------
// The dropdown's options everywhere (the pipeline's return step, the agent's,
// Other Actions), and the short names "Overridden from <name>" uses.
export const DISPOSITION_NAMES = Object.freeze({ return: 'RMA', scrap: 'Scrap RTN' })
export const DISPOSITION_OPTION_LABELS = Object.freeze({
  return: 'RMA - the part comes back',
  scrap: 'Scrap RTN - the customer keeps it',
})

// --- The customer's words --------------------------------------------------------

const NOUN = String.raw`(?:it|them|this|that|the\s+(?:part|parts|item|items|product|products|package|packages|box|return|order|unit))`
const SENT_BACK = String.raw`(?:sent|shipped|mailed|dropped\s+off|returned)`
// The part is already on its way back or back with us, in the customer's
// words. Past tense only: "I want to return it" or "how do I send it back"
// never count.
const ALREADY_RETURNED_RES = [
  new RegExp(String.raw`\b(?:i|we|i've|we've|i\s+have|we\s+have|i\s+had|we\s+had)\s+(?:already\s+|just\s+|now\s+)?returned\b`, 'i'),
  new RegExp(String.raw`\b(?:i|we|i've|we've|i\s+have|we\s+have|i\s+had|we\s+had)\s+(?:already\s+|just\s+)?(?:sent|shipped|mailed|dropped\s+off)\s+(?:${NOUN}\s+)?back\b`, 'i'),
  new RegExp(String.raw`\b${NOUN}\s+(?:was|were|has\s+been|have\s+been|had\s+been|got|is|are)\s+(?:already\s+|just\s+)?(?:${SENT_BACK})(?:\s+back)?\b`, 'i'),
  /\b(?:has|have|had)\s+(?:already\s+)?been\s+(?:returned|sent\s+back|shipped\s+back|mailed\s+back)\b/i,
  /\balready\s+(?:returned|sent\s+(?:it\s+|them\s+)?back|shipped\s+(?:it\s+|them\s+)?back)\b/i,
  /\b(?:returned|sent\s+back|shipped\s+back)\s+successfully\b|\bsuccessfully\s+(?:returned|sent\s+back|shipped\s+back)\b/i,
  /\btracking\s+(?:number\s+|#\s*|no\.?\s+)?(?:for|of)\s+(?:the|my|our|this)\s+(?:return(?:ed)?|package\s+i\s+(?:sent|returned))\b/i,
  /\breturn\s+tracking\s+(?:number|#|no\.?)\b/i,
]
// "I haven't returned it yet", "not sent back".
const NOT_RETURNED_RE = /\b(?:haven'?t|have\s+not|hasn'?t|has\s+not|didn'?t|did\s+not|not\s+yet|never)\s+(?:yet\s+)?(?:been\s+)?(?:returned|sent|shipped|mailed)\b/i

// The return is delivered to us, in the customer's words.
const DELIVERED_RES = [
  /\b(?:shows?|showing|showed|marked|says?|said|listed)\s+(?:as\s+|it\s+(?:was\s+|is\s+)?|that\s+it\s+(?:was\s+|is\s+)?)?delivered\b/i,
  /\b(?:was|were|has\s+been|have\s+been|been|got)\s+delivered\b/i,
  /\bdelivered\s+(?:back\s+)?to\s+(?:you|your|the\s+(?:warehouse|seller|fulfillment))/i,
  /\b(?:you|your\s+(?:warehouse|team|company))\s+(?:have\s+|has\s+|should\s+have\s+)?(?:received|got|signed\s+for)\b/i,
  /\b(?:returned|sent\s+back|shipped\s+back)\s+successfully\b|\bsuccessfully\s+(?:returned|delivered)\b/i,
  /\barrived\s+(?:back\s+)?(?:at|to)\s+(?:you|your)/i,
]
const NOT_DELIVERED_RE = /\b(?:not|n't|never|yet\s+to\s+be)\s+(?:been\s+|yet\s+)?(?:delivered|received|arrived)\b/i
// The return is on its way, in the customer's words.
const IN_TRANSIT_RES = [
  /\bin\s+transit\b/i,
  /\bon\s+(?:its|their|the)\s+way\s+(?:back\s+)?(?:to\s+you)?\b/i,
  /\b(?:hasn'?t|has\s+not|haven'?t|have\s+not)\s+(?:arrived|been\s+delivered|been\s+received)\b/i,
]

// Every valid tracking number in the text, once each, in order:
// [{ number, carrier, url }] (internal carrier codes, tracking-number.js).
const TRACKING_CANDIDATE_RE = /\b(1Z[0-9A-Z]{16}|TBA\d{12}|[A-Z]{2}\d{9}US|\d{12,34})\b/gi
export function trackingNumbersInText(text) {
  const out = []
  const seen = new Set()
  for (const m of String(text ?? '').matchAll(TRACKING_CANDIDATE_RE)) {
    const t = validTrackingNumber(m[1])
    if (!t || seen.has(t.number)) continue
    seen.add(t.number)
    out.push({ number: t.number, carrier: t.carrier, url: trackingPageUrl(t.number, t.carrier) })
  }
  return out
}

// "Returned to sender" is the carrier sending our shipment back, never the
// customer's return.
const RETURN_TO_SENDER_RE = /\b(?:returned|sent\s+back|being\s+returned)\s+to\s+(?:the\s+)?sender\b|\breturn\s+to\s+sender\b/gi
export function saysAlreadyReturned(text) {
  const s = String(text ?? '').replace(RETURN_TO_SENDER_RE, ' ')
  if (!s.trim()) return false
  if (!ALREADY_RETURNED_RES.some((re) => re.test(s))) return false
  // A negation with no positive claim elsewhere ("I haven't returned it yet").
  return !(NOT_RETURNED_RE.test(s) && !ALREADY_RETURNED_RES.some((re) => re.test(s.replace(NOT_RETURNED_RE, ''))))
}

// 'delivered' | 'in_transit' | null (the words don't say).
export const RETURN_CLAIM_DELIVERED = 'delivered'
export const RETURN_CLAIM_IN_TRANSIT = 'in_transit'
export function returnClaimStage(text) {
  const s = String(text ?? '')
  if (DELIVERED_RES.some((re) => re.test(s)) && !NOT_DELIVERED_RE.test(s)) return RETURN_CLAIM_DELIVERED
  if (IN_TRANSIT_RES.some((re) => re.test(s))) return RETURN_CLAIM_IN_TRANSIT
  return null
}

// The claim, or null when the customer does not say the part was already
// sent back: { stage, tracking }.
export function alreadyReturnedClaim(text) {
  if (!saysAlreadyReturned(text)) return null
  return { stage: returnClaimStage(text), tracking: trackingNumbersInText(text) }
}

// "UPS 1Z..." (internal carrier names: ATS for Amazon).
const INTERNAL_CARRIER = { UPS: 'UPS', USPS: 'USPS', FEDEX: 'FedEx', AMZ: 'ATS' }
export function trackingWithCarrier(t) {
  if (!t?.number) return ''
  const c = INTERNAL_CARRIER[String(t.carrier ?? '').toUpperCase()] ?? t.carrier ?? ''
  return `${c ? `${c} ` : ''}${t.number}`
}

// "the customer says the part was already returned (UPS 1Z...)".
export function alreadyReturnedReason(claim) {
  const list = (claim?.tracking ?? []).map(trackingWithCarrier).filter(Boolean)
  return `the customer says the part was already returned${list.length ? ` (${list.join(', ')})` : ''}`
}

// The step's tracking line and the stage step's link line.
export function customerReturnTrackingLine(t) {
  return `Customer's return tracking: ${trackingWithCarrier(t)}`
}
export function trackCustomerReturnLine(t) {
  return `Track the customer's return: ${trackingWithCarrier(t)}`
}

// The disposition decision: the part comes back (RMA) when the customer says
// it was already sent back and the order has no RMA for these items, whatever
// the scrap check said. null when the rule does not apply.
//   claim: alreadyReturnedClaim(); hasRmaForItems: an RMA already exists for
//   the selected parts; checked: the check's result ('return' | 'scrap' |
//   true | false | null).
// { disposition: 'return', overriddenFrom: 'scrap' | null, reason, tracking,
//   stage, shippingPaymentOption: 'customerShipsOwn' }.
export function alreadyShippedBackDecision({ claim = null, hasRmaForItems = false, checked = null } = {}) {
  if (!claim || hasRmaForItems) return null
  const word = checked === true || checked === 'return' ? 'return' : (checked === false || checked === 'scrap' ? 'scrap' : null)
  return {
    disposition: 'return',
    overriddenFrom: word === 'scrap' ? 'scrap' : null,
    reason: alreadyReturnedReason(claim),
    tracking: (claim.tracking ?? []).map((t) => ({ number: t.number, carrier: t.carrier, url: t.url ?? trackingPageUrl(t.number, t.carrier) })),
    stage: claim.stage ?? null,
    shippingPaymentOption: 'customerShipsOwn',
  }
}

// "Overridden from Scrap RTN" (+ ": <reason>." when the override has one).
export function overriddenFromText(fromWord, reason = null) {
  const name = DISPOSITION_NAMES[fromWord]
  if (!name) return null
  return `Overridden from ${name}${reason ? `: ${reason}.` : ''}`
}

// --- The reply template ------------------------------------------------------------
// The client's Refund folder: whether an RMA already exists for these parts
// (before this contact's step) and whether the return is delivered to us or
// in transit. return-status.js's RETURN_STATUS_TITLES keys.
export function refundStageTemplateKey({ hasRma = false, stage = null } = {}) {
  if (stage === RETURN_CLAIM_DELIVERED) return hasRma ? 'delivered' : 'deliveredNoRma'
  if (stage === RETURN_CLAIM_IN_TRANSIT) return hasRma ? 'inTransit' : 'inTransitNoRma'
  return null
}

// Titles compare by folder and normalized title: lower case, hyphen, en dash
// and em dash as one, spaces collapsed ("Refund Delivered — No RMA" is
// "Refund Delivered - No RMA").
export function normalizeRefundTitle(title) {
  return String(title ?? '').toLowerCase().replace(/[–—-]/g, '-').replace(/\s*-\s*/g, ' - ').replace(/\s+/g, ' ').trim()
}
export function isNoRmaRefundTitle(title) {
  return /^refund (?:delivered|in transit) - no rma$/.test(normalizeRefundTitle(title))
}

// The "No RMA" templates' RMA line: "your RMA # has now been generated:
// INSERTRMANUMBER." takes the RMA token (rmartn-cust-pays.js), filled when the
// return is processed, as the Reference ID is.
export function withNoRmaNumberToken(text, token) {
  return String(text ?? '').replace(/INSERTRMANUMBER/g, token)
}

// --- The note inside the RMA --------------------------------------------------------
// The amounts in the note, one sentence each:
// "Original Refund: $X.XX. Return Label Deduction: $0.00. Final Refund: $X.XX."
const money = (v) => `$${Number(v).toFixed(2)}`
const isAmount = (v) => v != null && v !== '' && Number.isFinite(Number(v))
export function refundAmountsNoteLine({ original = null, deduction = null, final = null } = {}) {
  const parts = []
  if (isAmount(original)) parts.push(`Original Refund: ${money(original)}.`)
  if (isAmount(deduction)) parts.push(`Return Label Deduction: ${money(deduction)}.`)
  if (isAmount(final)) parts.push(`Final Refund: ${money(final)}.`)
  return parts.join(' ')
}
const AMOUNTS_LINE_RE = /\s*(?:Original Refund: \$[\d,]+\.\d{2}\.)?\s*(?:Return Label Deduction: \$[\d,]+\.\d{2}\.)?\s*(?:Final Refund: \$[\d,]+\.\d{2}\.)\s*$/
// The note with its amounts line replaced (or added at the end), so a change
// of the deduction never leaves two.
export function withRefundAmountsNoteLine(note, amounts) {
  const base = String(note ?? '').replace(AMOUNTS_LINE_RE, '').replace(/\s+$/, '')
  const line = refundAmountsNoteLine(amounts ?? {})
  if (!line) return base
  return base ? `${base} ${line}` : line
}

// The customer's tracking in the note, when the note does not already name it.
export function withTrackingNoteLine(note, tracking = []) {
  let out = String(note ?? '').replace(/\s+$/, '')
  for (const t of tracking ?? []) {
    if (!t?.number || out.toUpperCase().includes(String(t.number).toUpperCase())) continue
    const line = `Provided ${trackingWithCarrier(t).replace(` ${t.number}`, '')} return tracking ${t.number}.`
    out = out ? `${out} ${line}` : line
  }
  return out
}
